import { Box, Flex, Spinner, Text } from '@chakra-ui/react'

import { IDTreatment360NewsAndPressReleasesModel } from 'config/common/baseModel/ida360/IDTreatment360NewsAndPressReleases'
import { useTableChartFilters } from 'contexts/TableChartFilters'
import { IBaseView } from 'interfaces/navigationPage.interface'
import { Virtuoso } from 'react-virtuoso'

import { useEffect, useMemo, useState } from 'react'
import { Article } from 'routes/dashboard/WidgetComponents/WidgetArticle/Article'
import {
  ArticleFiltersT,
  DefaultArticleFilters,
  useArticleFilters,
} from 'utils/useArticleFilters'
import { useFilteredArticles } from 'routes/dashboard/WidgetComponents/WidgetArticle/useFilteredArticles'
import { InlineLoading } from 'components'
import {
  getDefaultArticleFilters,
  getDiseaseFilter,
} from '../LatestAnalysisNews/LatestMedia'
import { isEqual } from 'lodash'

const Card = ({ children, disease }: { children: any; disease: string }) => {
  return (
    <Box w='350px'>
      <Flex mb={6} flexDir={'column'} justifyContent={'space-between'}>
        <Text
          fontSize='18px'
          fontWeight='semibold'
          whiteSpace={'nowrap'}
          mt='5px'
          mb='2px'
        >
          {disease} Candidates Latest Media
        </Text>
      </Flex>
      <Box
        border='1px solid'
        borderColor='gray3'
        w={'100%'}
        py='1rem'
        px='0.5rem'
        bg='white'
        rounded='md'
        overflow={'hidden'}
        pos='relative'
        h='calc(70vh)'
      >
        {children}
      </Box>
    </Box>
  )
}

function getAirtableName(disease: string) {
  if (disease === 'Botulism') {
    return 'outbreak_competitive_landscape_botulism'
  }
  return 'outbreak_pipeline_mpox'
}

const LatestMediaPipeline = ({ disease }: { disease: string }) => {
  const { tableFilters } = useTableChartFilters()

  const keyNews = 'keyNewsAlert'

  const view = {
    airtableName: getAirtableName(disease),
    airtableBase: 'lzdb',
    name: 'Default',
  } as IBaseView<any, any>
  const { options } = useArticleFilters(view)

  const model = IDTreatment360NewsAndPressReleasesModel

  const [filters, setFilters] = useState<ArticleFiltersT>(
    getDefaultArticleFilters(disease)
  )

  const candidateFilter = tableFilters.find(
    (filter) => filter.column === 'candidateName'
  )
  useEffect(() => {
    let newFilters = { ...DefaultArticleFilters, ...getDiseaseFilter(disease) }
    if (candidateFilter) {
      const newCandidates = candidateFilter.filterValue
        .map((candidate: any) => {
          const matchedCandidate = options.candidates.find(
            (cand) => cand.label === candidate.label
          )
          return {
            id: matchedCandidate?.id ?? candidate.value,
            value: matchedCandidate?.id ?? candidate.value,
            label: matchedCandidate?.label,
          }
        })
        .filter(Boolean)

      newFilters = {
        ...newFilters,
        candidates: newCandidates.length ? newCandidates : [],
      }
    }
    setFilters(newFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateFilter])

  const disableTracking = useMemo(
    () => isEqual(filters, getDefaultArticleFilters(disease)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disease]
  )

  const {
    data,
    canFetchMore,
    isFetched,
    isError,
    fetchMore,
    isFetchingNextPage,
  } = useFilteredArticles({
    maxItems: 10,
    model,
    view,
    filters,
    disableTracking,
  })

  if (isError) {
    return (
      <Card disease={disease}>
        <Flex w='100%' alignItems='center' justifyContent='center'>
          Sorry, something went wrong loading the latest media
        </Flex>
      </Card>
    )
  }
  if (!data?.length && isFetched) {
    return (
      <Card disease={disease}>
        <Flex justifyContent={'center'} alignItems={'center'} h='full'>
          <Text> No media</Text>
        </Flex>
      </Card>
    )
  }
  if (!data?.length) {
    return (
      <Card disease={disease}>
        <Flex justifyContent={'center'} alignItems={'center'} h='full'>
          <InlineLoading />
        </Flex>
      </Card>
    )
  }

  return (
    <Card disease={disease}>
      {!!data?.length && (
        <Box w='full' overflow={'hidden'} mt={'3px'} h='full'>
          <Virtuoso
            totalCount={data.length}
            itemContent={(index: number) => {
              const row = data[index]
              return (
                <Article
                  id={row.id}
                  modelEndpoint={'rss-article'}
                  airtableBase={view.airtableBase}
                  key={row.id}
                  url={row.link as string}
                  date={row.publishedAt as string}
                  title={row?.titleDisplay as string}
                  isKeyNews={!!row[keyNews]}
                  trackMediaClick={() => {}}
                />
              )
            }}
            endReached={() => {
              if (canFetchMore && !isFetchingNextPage) {
                fetchMore()
              }
            }}
          />
          {isFetchingNextPage && (
            <Flex w='full' justifyContent={'center'} mt='4px'>
              <Spinner />
            </Flex>
          )}
        </Box>
      )}
    </Card>
  )
}

export default LatestMediaPipeline
